import React, { useState } from "react";
import { Modal as MuiModal, Backdrop, Fade } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import SoftBox from "../../../components/SoftBox";
import { TEMPLATES, templateById } from "./templates";
import { widgetById } from "./widgetRegistry";
import "./customize.css";

/**
 * The template picker — built on the SAME modal shell + overlay as the
 * Players-management modal (CustomizeViewModal): a MUI Modal + Fade, a frosted
 * white gradient paper (0.70→0.60), 8px radius, luminous border, and the app's
 * standard dim overlay (rgba(15,23,42,0.16) + blur(3px)). This keeps every
 * modal in the app consistent.
 *
 * Choosing a template hands the layout AND its name back; anything destructive
 * is confirmed by ConfirmWipe before it lands (see Homepage).
 */
const FONT = `"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif`;

const SHELL = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: { mini: "92%", sm: "720px" },
  background: "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.84) 100%)",
  boxShadow: "0px 24px 48px -8px rgba(16,24,40,0.18), inset 0px 1px 0px 0px rgba(255,255,255,0.7)",
  borderRadius: "8px",
  backdropFilter: "blur(30px) saturate(185%)",
  WebkitBackdropFilter: "blur(30px) saturate(185%)",
  border: "1px solid rgba(255,255,255,0.6)",
  overflow: "hidden",
  fontFamily: FONT,
};

const HAIRLINE = "rgba(15,23,42,0.10)";

// A proportional wireframe of a template's layout, drawn on the same 24-col
// grid so the thumbnail matches what actually lands on the board.
export const MiniLayout = ({ layout }) => {
  const rows = Math.max(1, ...layout.map((b) => b.y + b.h));
  return (
    <div className="gmbw-mini" aria-hidden="true">
      {layout.map((b) => {
        const def = widgetById(b.i);
        const Thumb = def?.thumb;
        return (
          <span
            key={b.i}
            className="gmbw-mini-b"
            style={{
              left: `${(b.x / 24) * 100}%`,
              width: `${(b.w / 24) * 100}%`,
              top: `${(b.y / rows) * 100}%`,
              height: `${(b.h / rows) * 100}%`,
            }}
          >
            {/* THE COMPONENT ITSELF, drawn small — not a grey bar standing in
                for it. These are the same previews the component list uses
                (widgetPreviews), so a template can never show something the
                component doesn't look like, and it updates itself whenever a
                component's design changes. `tiny` drops the detail that stops
                reading at this size. */}
            <i>{Thumb ? <Thumb tiny /> : null}</i>
          </span>
        );
      })}
    </div>
  );
};

const TemplatePicker = ({ open, onClose, onApply, brandVars, boardCount = 0 }) => {
  const [selected, setSelected] = useState((TEMPLATES.find((t) => t.recommended) || TEMPLATES[0]).id);

  const close = () => {
    setSelected("technician");
    onClose();
  };

  let selectedName = templateById(selected)?.name;
  if (selected === "blank") selectedName = "Blank canvas";
  else if (selected === "keep") selectedName = "your current board";

  const onUse = () => {
    if (selected === "keep") {
      close();
      return;
    }
    const name = selected === "blank" ? "Blank canvas" : templateById(selected)?.name;
    onApply(selected === "blank" ? [] : templateById(selected)?.layout || [], name);
    close();
  };

  return (
    <MuiModal
      open={!!open}
      onClose={close}
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 240,
        sx: {
          backgroundColor: "rgba(15,23,42,0.16)",
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
        },
      }}
      closeAfterTransition
    >
      <Fade in={!!open}>
        <SoftBox sx={SHELL} style={brandVars}>
          {/* Header */}
          <SoftBox
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              padding: "18px 22px 14px",
              borderBottom: `1px solid ${HAIRLINE}`,
            }}
          >
            <SoftBox sx={{ display: "flex", alignItems: "flex-start", gap: "11px", minWidth: 0 }}>
              <SoftBox
                sx={{
                  width: "34px",
                  height: "34px",
                  flexShrink: 0,
                  borderRadius: "8px",
                  display: "grid",
                  placeItems: "center",
                  background: "var(--gmbw-primary-soft, rgba(27,89,142,0.10))",
                  color: "var(--gmbw-primary, #1B598E)",
                }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="9" rx="1.4" />
                  <rect x="14" y="3" width="7" height="5" rx="1.4" />
                  <rect x="14" y="12" width="7" height="9" rx="1.4" />
                  <rect x="3" y="16" width="7" height="5" rx="1.4" />
                </svg>
              </SoftBox>
              <SoftBox sx={{ minWidth: 0 }}>
              <SoftBox sx={{ fontFamily: FONT, fontSize: "15px", fontWeight: 500, letterSpacing: 0, color: "#0F172A" }}>
                Start from a template
              </SoftBox>
              <SoftBox sx={{ marginTop: "2px", fontFamily: FONT, fontSize: "12px", fontWeight: 400, color: "#98A2B3" }}>
                Pick a ready-made layout — tweak anything after.
              </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox
              component="button"
              type="button"
              onClick={close}
              aria-label="Close"
              sx={{
                width: "32px",
                height: "32px",
                minWidth: "32px",
                borderRadius: "8px",
                padding: 0,
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                background: "rgba(15,23,42,0.05)",
                border: `1px solid ${HAIRLINE}`,
                color: "#0F172A",
                "& svg": { fontSize: "1.1rem" },
                "&:hover": { background: "rgba(15,23,42,0.09)" },
              }}
            >
              <CloseIcon />
            </SoftBox>
          </SoftBox>

          {/* Body */}
          <SoftBox sx={{ padding: "20px 22px 4px", maxHeight: "min(70vh, 560px)", overflowY: "auto" }}>
            <div className="gmbw-tpl-grid">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`gmbw-tpl-card${selected === t.id ? " selected" : ""}`}
                  onClick={() => setSelected(t.id)}
                >
                  {t.recommended && <span className="gmbw-tpl-badge">Recommended</span>}
                  <div className="gmbw-tpl-name">{t.name}</div>
                  <div className="gmbw-tpl-blurb">{t.blurb}</div>
                  <MiniLayout layout={t.layout} />
                </button>
              ))}

              <div className="gmbw-tpl-mini-col">
                <button
                  type="button"
                  className={`gmbw-tpl-tile${selected === "keep" ? " selected" : ""}`}
                  onClick={() => setSelected("keep")}
                >
                  <span className="gmbw-tpl-tile-ic">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
                  </span>
                  <span className="gmbw-tpl-tile-name">Keep current</span>
                </button>
                <button
                  type="button"
                  className={`gmbw-tpl-tile dashed${selected === "blank" ? " selected" : ""}`}
                  onClick={() => setSelected("blank")}
                >
                  <span className="gmbw-tpl-tile-ic">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                  </span>
                  <span className="gmbw-tpl-tile-name">Blank canvas</span>
                </button>
              </div>
            </div>
          </SoftBox>

          {/* Footer */}
          <SoftBox
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              padding: "14px 22px 18px",
              borderTop: `1px solid ${HAIRLINE}`,
              background: "rgba(15,23,42,0.03)",
            }}
          >
            <SoftBox
              component="button"
              type="button"
              onClick={close}
              sx={{
                height: "40px",
                padding: "0 16px",
                background: "transparent",
                border: `1px solid ${HAIRLINE}`,
                borderRadius: "8px",
                color: "#0F172A",
                fontFamily: FONT,
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": { background: "rgba(15,23,42,0.04)" },
              }}
            >
              Cancel
            </SoftBox>
            <SoftBox
              component="button"
              type="button"
              onClick={onUse}
              sx={{
                height: "40px",
                padding: "0 18px",
                border: 0,
                borderRadius: "8px",
                cursor: "pointer",
                background: "var(--gmbw-primary, #1B598E)",
                color: "#fff",
                fontFamily: FONT,
                fontSize: "13px",
                fontWeight: 500,
              }}
            >
              {selected === "keep"
                ? "Keep current board"
                : `Use ${selectedName}${boardCount ? "…" : ""}`}
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </Fade>
    </MuiModal>
  );
};

export default TemplatePicker;
